import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { stripDecimalsAndFormat, calculatePercentage } from 'utils/misc';
import reportChartSelector from 'selectors/reportChartSelector';
import Tooltip from '@material-ui/core/Tooltip';
import millify from 'utils/millify';
import { MIN_DESKTOP_WIDTH, MAX_SMALL_SIZE } from 'constants/misc';
import classNames from 'classnames';
import StatCard from './StatCard';

const styles = () => ({
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridTemplateRows: 'repeat(2, 1fr)',
    gap: '1.25rem 1.25rem',
    marginBottom: '1rem',
    [`@media screen and (min-width: ${MIN_DESKTOP_WIDTH}px) and (max-width: ${MAX_SMALL_SIZE}px)`]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: 'repeat(3, 1fr)',
    },
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  valueCard: {
    gridArea: '1',
    height: '5.25rem',
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      margin: '0.625rem 0',
    },
  },
  potentialReach: {
    gridArea: '2',
  },
  graphCard: {
    height: '5.25rem',
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      margin: '0.625rem 0',
    },
  },
});

class Stats extends PureComponent {
  static propTypes = {
    classes: PropTypes.element.isRequired,
    report: PropTypes.shape({
      total_contacts: PropTypes.number.isRequired,
      potential_reach: PropTypes.number.isRequired,
      delivered: PropTypes.number.isRequired,
      bounced: PropTypes.number.isRequired,
      opened: PropTypes.number.isRequired,
      replied: PropTypes.number.isRequired,
      interested: PropTypes.number.isRequired,
      people_contacted: PropTypes.number.isRequired,
      attempted_sends_unique: PropTypes.number.isRequired,
      expanded_interest: PropTypes.number.isRequired,
    }).isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      delivered: PropTypes.number,
      opened: PropTypes.number,
      replied: PropTypes.number,
      interested: PropTypes.number,
    })),
  };

  static defaultProps = {
    data: [],
  };

  render() {
    const {
      total_contacts: totalContacts,
      potential_reach: potentialReach,
      delivered,
      bounced,
      opened,
      replied,
      interested,
      people_contacted: peopleContacted,
      attempted_sends_unique: attemptedSendsUnique,
      expanded_interest: expandedInterest,
    } = this.props.report;
    const { classes, data } = this.props;

    return (
      <React.Fragment>
        <div className={classes.statsContainer}>
          <Tooltip title={totalContacts}>
            <StatCard
              value={String(millify(totalContacts)).toUpperCase()}
              tooltip={stripDecimalsAndFormat(totalContacts)}
              label="Total Contacts"
              className={classes.valueCard}
            />
          </Tooltip>
          <Tooltip title={potentialReach}>
            <StatCard
              value={String(millify(potentialReach)).toUpperCase()}
              tooltip={stripDecimalsAndFormat(potentialReach)}
              label="Potential Reach"
              className={classNames(classes.valueCard, classes.potentialReach)}
            />
          </Tooltip>
          <Tooltip title={delivered}>
            <StatCard
              value={delivered}
              tooltip={stripDecimalsAndFormat(delivered)}
              label="Delivered"
              dataKey="delivered"
              graphData={data}
              info="Successfully sent emails (includes each step in pitches)."
              color="blue"
              className={classes.graphCard}
            />
          </Tooltip>
          <StatCard
            value={`${calculatePercentage(peopleContacted, opened)}%`}
            label="Opened"
            dataKey="opened"
            graphData={data}
            info="Contacts that opened at least one email in the sequence."
            superScript={opened}
            color="green"
            className={classes.graphCard}
          />
          <StatCard
            value={`${calculatePercentage(peopleContacted, replied)}%`}
            label="Replied"
            dataKey="replied"
            graphData={data}
            info="Contacts that replied to a sequence (excludes auto-replies)."
            superScript={replied}
            color="blue"
            className={classes.graphCard}
          />
          <StatCard
            value={`${calculatePercentage(peopleContacted, interested)}%`}
            label="Interested"
            dataKey="interested"
            superScript={interested}
            graphData={data}
            info="Replies to pitches categorized as interested."
            color="pink"
            className={classes.graphCard}
          />
          <StatCard
            value={`${calculatePercentage(peopleContacted, expandedInterest)}%`}
            label="Expanded Interest"
            dataKey="expandedInterest"
            superScript={expandedInterest}
            graphData={data}
            info={
              'Replies to pitches categorized as interested, \
              not now and sponsorship* (*in case where setting for sponsorship = yes)'
            }
            color="blue"
            className={classes.graphCard}
          />
          <StatCard
            value={`${calculatePercentage(attemptedSendsUnique, bounced)}%`}
            label="Bounced"
            dataKey="bounced"
            superScript={bounced}
            graphData={data}
            info="Contacts that bounced."
            color="pink"
            className={classes.graphCard}
          />
        </div>
      </React.Fragment>
    );
  }
}

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(
    state => ({
      report: state.report,
      data: reportChartSelector(state.report),
    }),
    { },
  ),
)(Stats);
